import React from 'react';
import type {
  NewsComparisonBundle,
  NewsContrastRow,
  StructuredNewspaperArticle,
} from '@interactive-room';
import { shouldUseNewspaperColumns } from '@interactive-room';
import { PrintRichText } from './PrintRichText';

function NewspaperMasthead({ article }: { article: StructuredNewspaperArticle }) {
  const body = article.body.trim();
  const dropCap = body[0] ?? '';
  const rest = body.slice(1);
  const useColumns = shouldUseNewspaperColumns(body);

  return (
    <article className="print-newspaper">
      {article.outlet ? <p className="print-newspaper__outlet">{article.outlet}</p> : null}
      <header className="print-newspaper__masthead">
        {article.kicker ? <p className="print-newspaper__kicker">{article.kicker}</p> : null}
        <h4 className="print-newspaper__headline">{article.headline}</h4>
        {article.deck ? <p className="print-newspaper__deck">{article.deck}</p> : null}
      </header>
      <div
        className={`print-newspaper__body${useColumns ? ' print-stimulus__body--cols-2' : ''}`}
      >
        {dropCap ? <span className="print-stimulus__drop-cap">{dropCap}</span> : null}
        <PrintRichText text={rest || body} />
      </div>
    </article>
  );
}

export function PrintStructuredNewspaper({
  article,
  className = '',
}: {
  article: StructuredNewspaperArticle;
  className?: string;
}) {
  return (
    <div className={`print-themed-reading print-themed-reading--newspaper ${className}`.trim()}>
      <NewspaperMasthead article={article} />
    </div>
  );
}

export function PrintEditorialContrastTable({
  rows,
  leftHeader = 'Medio A',
  rightHeader = 'Medio B',
  title,
}: {
  rows: NewsContrastRow[];
  leftHeader?: string;
  rightHeader?: string;
  title?: string;
}) {
  if (!rows.length) return null;
  return (
    <div className="print-editorial-table-wrap">
      {title ? <p className="print-editorial-table__title">{title}</p> : null}
      <table className="print-editorial-table">
        <thead>
          <tr>
            <th scope="col">Aspecto</th>
            <th scope="col">{leftHeader}</th>
            <th scope="col">{rightHeader}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.label}-${row.left}`}>
              <th scope="row">{row.label}</th>
              <td>{row.left}</td>
              <td>{row.right}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PrintPairedTerms({
  pairs,
  leftHead = 'Término',
  rightHead = 'Par',
}: {
  pairs: Array<{ left: string; right: string }>;
  leftHead?: string;
  rightHead?: string;
}) {
  if (!pairs.length) return null;
  const rightShuffled = [...pairs].sort((a, b) => a.right.localeCompare(b.right, 'es'));

  return (
    <div className="print-vincula-grid print-paired-terms">
      <div className="print-vincula-col print-vincula-col--a">
        <p className="print-vincula-head">{leftHead}</p>
        <ul className="print-vincula-list print-vincula-list--a">
          {pairs.map((pair, i) => (
            <li key={`l-${i}`}>
              <span className="print-vincula-label">{pair.left}</span>
              <span className="print-vincula-circle" aria-hidden>
                ○
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="print-vincula-bridge" aria-hidden />
      <div className="print-vincula-col print-vincula-col--b">
        <p className="print-vincula-head">{rightHead}</p>
        <ul className="print-vincula-list print-vincula-list--b">
          {rightShuffled.map((pair, i) => (
            <li key={`r-${i}`}>
              <span className="print-vincula-circle" aria-hidden>
                ○
              </span>
              <span className="print-vincula-label">{pair.right}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function PrintNewsComparison({
  bundle,
  className = '',
}: {
  bundle: NewsComparisonBundle;
  className?: string;
}) {
  const [left, right] = bundle.articles;
  return (
    <div className={`print-news-comparison ${className}`.trim()}>
      <div className="print-news-comparison__grid">
        <PrintStructuredNewspaper article={left} />
        <PrintStructuredNewspaper article={right} />
      </div>
      <PrintEditorialContrastTable
        rows={bundle.matrix}
        leftHeader={left.outlet || 'Noticia 1'}
        rightHeader={right.outlet || 'Noticia 2'}
        title="Contraste editorial"
      />
      {bundle.reflection ? (
        <p className="print-news-comparison__reflection">
          <span className="print-news-comparison__reflection-label">Reflexión</span>
          {bundle.reflection}
        </p>
      ) : null}
    </div>
  );
}
